// non_ui/clientUnified.js
// Single client for auth + user management + authed fetch.
// Replace imports in your app to import from "./non_ui/clientUnified".

/** ===== Config ===== */
export const API_BASE = "https://asia-south1-trutz-232d9.cloudfunctions.net";
const TOKEN_KEY = "admin_id_token";
const ADMIN_ID_KEY = "admin_id"

/** ===== Token helpers ===== */
export function setToken(t) { if (t) localStorage.setItem(TOKEN_KEY, t); }
export function getToken() { return localStorage.getItem(TOKEN_KEY) || ""; }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); }

export function setAdminId(t) { if (t) localStorage.setItem(ADMIN_ID_KEY, t); }
export function getAdminId() { return localStorage.getItem(ADMIN_ID_KEY) || ""; }
export function clearAdminId() { localStorage.removeItem(ADMIN_ID_KEY); }


/** ===== Fetch with Authorization header (if token exists) ===== */
export async function authedFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) throw new Error((data && (data.error || data.message)) || `HTTP ${res.status}`);
  return data;
}

/** ===== Auth: login/logout ===== */
export async function loginAdmin(email, password) {
  const res = await fetch(`${API_BASE}/loginUser`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: String(email || "").trim(), password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && (data.error || data.message)) || `HTTP ${res.status}`);
  if (!data.idToken) throw new Error("No token returned from server");
  // Some backends also return user { uid, role }. We just persist token here.
  setToken(data.idToken);
  setAdminId(data.uid);
  return data; // { success, uid, idToken, ... }
}

export function logout() { 
  clearToken(); 
  clearAdminId();
}

/** ===== Users API ===== */

// List users with optional filters: name (prefix), email (prefix), organisation (exact)
export async function listUsers({ name, email, organisation } = {}) {
  const params = new URLSearchParams();
  if (name) params.set("name", name);
  if (email) params.set("email", email);
  if (organisation) params.set("organisation", organisation);
  const qs = params.toString();
  return authedFetch(`/userList${qs ? `?${qs}` : ""}`, { method: "GET" });
}

// Create user
export async function createUser({ email, password, name, role = "user", organisation }) {
  if (!email || !password || !name || !organisation) {
    throw new Error("email, password, name, organisation are required");
  }
  return authedFetch(`/createUser`, {
    method: "POST",
    body: JSON.stringify({ email, password, name, role, organisation }),
  });
}

// Delete user
export async function deleteUser(uid) {
  if (!uid) throw new Error("uid is required");
  return authedFetch(`/deleteUser`, {
    method: "POST",
    body: JSON.stringify({ uid }),
  });
}

/** ===== Password helpers ===== */

// Generate a client-side temporary password (if you use the /updatePassword flow)
export function generateTempPassword(length = 6) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

/**
 * Issue a temporary password to a user.
 * Prefers the backend endpoint /setTempPasswordAndNotify (which generates & emails),
 * and falls back to /updatePassword (client generates and sets).
 *
 * @param {string} uidOrEmail - target user uid or email
 * @param {number} length - temp password length (only used if fallback path)
 * @returns {Promise<{method: 'notify'|'direct', tempPassword?: string}>}
 */
export async function issueTempPassword(uidOrEmail, length = 6) {
  if (!uidOrEmail) throw new Error("uid or email required");

  const adminId = getAdminId(); // <- assuming this returns the admin's UID or ID token?
  const temp = generateTempPassword(length);
  try {
    await authedFetch(`/updatePassword`, {
      method: "POST",
      body: JSON.stringify({
        adminId,
        emailOrUid: uidOrEmail,
        newPassword: temp,
        revoke: true,
      }),
    });
    
    // return { method: "direct", tempPassword: temp };
    return temp;
  } catch (e) {
    alert( "issueTempPassword : " + String(e));
  }
}
